/**
 * Character Picker Component
 *
 * Displays a grid of 9 patient characters that players can select.
 * - Characters become unavailable immediately when another player selects them
 * - Each player must select a character before the game can start
 * - Selected character is highlighted with the character's color
 */

import { motion } from "framer-motion";
import type { PlayerInfo } from "../../../../types/game";
import type { Character } from "../../../../types/terriblePsychiatrist";

// Map avatar names to emoji (from backend YAML)
// Animal characters representing mental health archetypes
const AVATAR_EMOJI: Record<string, string> = {
  raccoon: "🦝",
  opossum: "🐀",
  sloth: "🦥",
  honey_badger: "🦡",
  squirrel: "🐿️",
  cat: "🐱",
  pigeon: "🐦",
  golden_retriever: "🐕",
  hedgehog: "🦔",
};

interface CharacterPickerProps {
  characters: Character[];
  selectedCharacterId: string | null;
  playerId: string;
  allPlayers: PlayerInfo[];
  onSelectCharacter: (characterId: string) => void;
  disabled?: boolean;
}

export function CharacterPicker({
  characters,
  selectedCharacterId,
  playerId,
  allPlayers,
  onSelectCharacter,
  disabled = false,
}: CharacterPickerProps) {
  // Get player name who selected a character
  const getPlayerName = (playerIdToFind: string | null): string | null => {
    if (!playerIdToFind) return null;
    const player = allPlayers.find((p) => p.player_id === playerIdToFind);
    return player?.display_name || null;
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <h3
        className="text-lg font-bold mb-4 text-center"
        style={{ color: "var(--color-text-primary)" }}
      >
        Choose Your Character
      </h3>

      <div className="grid grid-cols-3 gap-3">
        {characters.map((character) => {
          const isSelected = character.id === selectedCharacterId;
          const isTakenByOther =
            !character.is_available && character.selected_by !== playerId;
          const takenByName = getPlayerName(character.selected_by);
          const emoji = AVATAR_EMOJI[character.avatar] || "👤";

          return (
            <motion.button
              key={character.id}
              onClick={() =>
                !disabled &&
                character.is_available &&
                onSelectCharacter(character.id)
              }
              disabled={disabled || isTakenByOther}
              whileHover={
                !disabled && character.is_available ? { scale: 1.02 } : {}
              }
              whileTap={
                !disabled && character.is_available ? { scale: 0.98 } : {}
              }
              className={`
                relative p-3 rounded-neo-sm text-left transition-all
                ${isSelected ? "ring-2 ring-offset-2" : ""}
                ${isTakenByOther ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              `}
              style={{
                backgroundColor: isSelected
                  ? `${character.color}20`
                  : "var(--color-surface)",
                border: isSelected
                  ? `2px solid ${character.color}`
                  : "var(--neo-border-medium)",
                borderColor: isSelected
                  ? character.color
                  : "var(--color-neutral)",
              }}
              data-testid={`character-${character.id}`}
            >
              {/* Character Avatar */}
              <div
                className="text-3xl mb-2 w-12 h-12 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: `${character.color}30`,
                }}
              >
                {emoji}
              </div>

              {/* Character Name */}
              <div
                className="font-bold text-sm mb-1"
                style={{
                  color: isSelected
                    ? character.color
                    : "var(--color-text-primary)",
                }}
              >
                {character.name}
              </div>

              {/* Character Description */}
              <div
                className="text-xs leading-tight"
                style={{ color: "var(--color-text-muted)" }}
              >
                {character.description}
              </div>

              {/* Selected/Taken Indicator */}
              {isSelected && (
                <div
                  className="absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-bold"
                  style={{
                    backgroundColor: character.color,
                    color: "white",
                  }}
                >
                  YOU
                </div>
              )}

              {isTakenByOther && takenByName && (
                <div
                  className="absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: "var(--color-neutral)",
                    color: "var(--color-text-muted)",
                  }}
                >
                  {takenByName}
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Hint text */}
      <p
        className="text-center text-sm mt-4"
        style={{ color: "var(--color-text-muted)" }}
      >
        {selectedCharacterId
          ? "Character selected! Waiting for other players..."
          : "Select a character to play as. Each player must choose a unique character."}
      </p>
    </div>
  );
}
